import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  DOCUMENT,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, forkJoin, map, take, takeUntil } from 'rxjs';
import { AuthService } from '@core/auth.service';
import * as fromUserProfile from '@modules/shared/user-profile.reducer';
import { CommonService } from '@shared/services/common.service';
import { CoreService } from '@services/core.service';
import { Application } from '@enums/application.enums';

import {
  Fields,
  LEGAL_LINK_LABELS,
  SIDE_BAR_MODULE,
  VERIFICATION_STATUS
} from '@constants/constant';
import { BsModalService } from 'ngx-bootstrap/modal';
import { InfoModalComponent } from '@components/info-modal/info-modal.component';
import {
  ApplicationStatusEnums,
  CommonEnums,
  PERMISSION_TYPE
} from '@shared/enums/common.enum';
import { PERMISSION_KEYS } from '@shared/constants/permissions.contant';
import { environment } from '@environments/environment';
import { isWhiteLabelCoverPagePublished } from '@utils/common.utils';
import { appRoutingUrl } from '@enums/comman.enums';
import { PwaService } from '@core/pwa.service';
import { WhiteLabelConfig } from '@models/common.interface';
import { PermissionService } from '@shared/services/permissions.service';
import { ResourcesService } from '@modules/resources/services/resources-service';
import { ROUTE_PATHS } from '@constants/routes';
import { SIDEBAR_TABS } from '@shared/constants/tab-approval.constant';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { CREATORS_CONST } from '@modules/creators-home/constants/creatorsConstants';
import { POST_TYPE } from '@modules/dashboard/constants/home-strings.constant';
import { InsightsStateService } from '@modules/insights/services/insights-state.service';
import {
  INSIGHT_TAB,
  USER_ACTIONS
} from '@modules/insights/components/constant';
import { SIDEBAR_CONSTANTS } from './sidebar.constants';
import { HeaderMenuLabelService } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-menu-label.service';
import { HEADER_LAYOUT_CONSTANTS } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.constants';
import { LoginDialogComponent } from '@shared/components/login-dialog/login-dialog.component';
import { ButtonComponent } from '@components/button/button.component';
import { SkeletonComponent } from '@components/skeleton/skeleton.component';
import { UserProfileComponent } from '@shared/components/user-profile/user-profile.component';
import { CreatorRequirementsModalComponent } from '@modules/edit-preference/become-a-creator/components/creator-requirements-modal/creator-requirements-modal.component';
import { DisableRightClickDirective } from '@modules/shared/directive/disable-right-click.directive';
import { MobileOptimizationDirective } from '@app-shared/directives/mobile-optimization.directive';
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';
import { ShowTabByApprovalPipe } from '@shared/pipes/show-tab-by-approval.pipe';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['sidebar.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    ButtonComponent,
    SkeletonComponent,
    UserProfileComponent,
    CreatorRequirementsModalComponent,
    DisableRightClickDirective,
    MobileOptimizationDirective,
    HasPermissionPipe,
    ShowTabByApprovalPipe
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {
  readonly sidebarRoot = viewChild<ElementRef<HTMLElement>>('sidebarRoot');
  readonly creatorRequirementsModal = viewChild<any>('creatorRequirementsModal');
  userProfile: any;
  isSidebarLoading: boolean = true;
  readonly skeletonActions = [1, 2];
  readonly skeletonNavItems = [1, 2, 3, 4];
  whiteLabelConfigData: any;
  isWhiteLabel: boolean;
  userBadges = {
    messageCount: '',
    unreadActivityCount: '',
    my_fulfillment_requests_count: '',
    refund_requests_count: '',
    merchandise_awaiting_fan_confirmation_count: '',
    experience_awaiting_fan_confirmation_count: ''
  };
  isSticky = false;
  private scrollTicking = false;
  private readonly STICKY_HYSTERESIS = 50; // Buffer to prevent flickering
  applicationStatusEnums = ApplicationStatusEnums;
  successModalField: any;
  pendingModalField: any;
  failureModalField: any;
  applicationRejectModalField: any;
  permissionKeys = PERMISSION_KEYS;
  isCreatorRequestResubmitted: boolean = false;
  PERMISSION_TYPE = PERMISSION_TYPE;
  commonEnums = CommonEnums;
  legalLinkLabels = LEGAL_LINK_LABELS;
  whiteLabelConfig: WhiteLabelConfig;
  walletRoute: string = '/wallet/my-purchases';
  totalResourcesCount: number = 0;
  appRoutingUrl = appRoutingUrl;
  destroy$ = new Subject<void>();
  Fields = Fields;
  ROUTES = ROUTE_PATHS;
  CREATORS_CONST = CREATORS_CONST;
  readonly postType = POST_TYPE;
  SIDEBAR_TABS = SIDEBAR_TABS;
  SIDEBAR_CONSTANTS = SIDEBAR_CONSTANTS;
  contentFeedLabel: string = SIDEBAR_CONSTANTS.labels.FEED;
  isSidebarReady = false;

  constructor(
    private readonly router: Router,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    public readonly authService: AuthService,
    private readonly modalService: BsModalService,
    public cs: CommonService,
    private readonly coreService: CoreService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    public pwaService: PwaService,
    private readonly ps: PermissionService,
    private readonly resourcesService: ResourcesService,
    public mobileBridgeService: MobileBridgeService,
    private readonly insightsStateService: InsightsStateService,
    private readonly headerMenuLabelService: HeaderMenuLabelService,
    private readonly cdr: ChangeDetectorRef
  ) {
    const config = this.cs.getWhiteLabelConfiguration();
    if (config) {
      this.whiteLabelConfigData = config.user;
      this.whiteLabelConfig = config.data.whiteLabel;
      this.isWhiteLabel = true;
    }
    afterNextRender(() => {
      this.isSidebarReady = true;
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.initVerificationModals();
    if (this.authService.isAuthenticated()) {
      this.isSidebarLoading = true;
      this.getWalletRoute();
      this.getBadges();
      this.userProfileSubscription();
      this.resourcesService.fetchResourcesCount();
    } else {
      this.isSidebarLoading = false;
    }
    this.getMessageCount();
    this.subscribeToForceLogout();

    this.cs.totalUnreadResourcesCountSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.totalResourcesCount = count;
      });
    this.headerMenuLabelService.hydrateFromCache();
    this.syncContentFeedLabel();
    this.headerMenuLabelService.itemsStream
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncContentFeedLabel();
      });
  }

  get showSidebarSkeleton(): boolean {
    if (!this.cs.isBrowser || !this.isSidebarReady) {
      return true;
    }
    if (this.isPwaOrMobileApp && this.isMobileNavViewport()) {
      return false;
    }
    return (
      (this.isSidebarLoading && this.authService.isLogin) || !this.cs.isBrowser
    );
  }

  get showSidebarContent(): boolean {
    return (
      this.cs.isBrowser && this.isSidebarReady && !this.showSidebarSkeleton
    );
  }

  get isPwaOrMobileApp(): boolean {
    return (
      this.mobileBridgeService.isMobileApp ||
      (this.cs.isBrowser && !!window.ReactNativeWebView) ||
      this.pwaService.isRunningAsPWA()
    );
  }

  get showGuestMobileNav(): boolean {
    return (
      !this.authService.isLogin &&
      this.isPwaOrMobileApp &&
      this.isMobileNavViewport()
    );
  }

  get showLoggedInNav(): boolean {
    return (
      this.authService.isLogin && !(this.isSidebarLoading && this.isWhiteLabel)
    );
  }

  get showSidebarNav(): boolean {
    return this.showGuestMobileNav || this.showLoggedInNav;
  }

  get canUseHostOrWhiteLabelPostActions(): boolean {
    return (
      (!this.isWhiteLabel && this.userProfile?.is_host_owner) ||
      (this.isWhiteLabel &&
        this.userProfile?.gsi4pk === this.whiteLabelConfigData?.gsi4pk)
    );
  }

  get isBecomeCreatorButtonDisabled(): boolean {
    return (
      !this.isCreatorRequestResubmitted &&
      (this.isCreatorRequestPending || this.isCreatorRequestFailed)
    );
  }

  get becomeCreatorButtonClass(): string {
    const baseClass = 'btn btn-primary btn-outlined w-100';
    return this.isBecomeCreatorButtonDisabled
      ? `${baseClass} fade-out`
      : baseClass;
  }

  get walletNotificationCount(): number {
    return (
      this.toBadgeCount(this.userBadges?.my_fulfillment_requests_count) +
      this.toBadgeCount(this.userBadges?.refund_requests_count) +
      this.toBadgeCount(
        this.userBadges?.merchandise_awaiting_fan_confirmation_count
      ) +
      this.toBadgeCount(
        this.userBadges?.experience_awaiting_fan_confirmation_count
      )
    );
  }

  private toBadgeCount(value: string | number | null | undefined): number {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
  }

  get showGuestCreatorsNav(): boolean {
    return this.showGuestMobileNav && !this.isWhiteLabel;
  }

  /**
   * On a published white-label cover page site `/` renders the cover page, so the
   * creator feed needs its own nav entry alongside Home.
   */
  get showWhiteLabelFeedNav(): boolean {
    return (
      !!this.isWhiteLabel &&
      isWhiteLabelCoverPagePublished() &&
      !!this.whiteLabelConfigData?.user_name
    );
  }

  get whiteLabelFeedRoute(): string {
    return `/${this.whiteLabelConfigData?.user_name}`;
  }

  get desktopStickyStyles(): Record<string, string> | null {
    if (!this.isSticky || this.isMobileNavViewport()) {
      return null;
    }
    return {
      position: 'sticky',
      top: SIDEBAR_CONSTANTS.MOBILE_NAV.STICKY_TOP
    };
  }

  private syncContentFeedLabel(): void {
    const feedUrl = this.whiteLabelConfigData?.user_name
      ? `/${this.whiteLabelConfigData.user_name}`
      : '';
    this.contentFeedLabel =
      this.headerMenuLabelService.getLabelById(
        HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS,
        ''
      ) ||
      this.headerMenuLabelService.getLabelByUrl(feedUrl, '') ||
      SIDEBAR_CONSTANTS.labels.FEED;
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: any) {
    if (this.cs.isBrowser && !this.scrollTicking) {
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.updateStickyState(event);
        this.scrollTicking = false;
      });
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isMobileNavViewport() && this.isSticky) {
      this.isSticky = false;
    }
  }

  private isMobileNavViewport(): boolean {
    return (
      this.cs.isBrowser &&
      window.innerWidth <= SIDEBAR_CONSTANTS.MOBILE_NAV.MAX_WIDTH
    );
  }

  private updateStickyState(event: any): void {
    if (this.isMobileNavViewport()) {
      if (this.isSticky) {
        this.isSticky = false;
      }
      return;
    }
    const scrollTop = event.target.scrollingElement.scrollTop;
    const footer = this.document.querySelector('.footer') as HTMLElement | null;
    const sidebar = this.sidebarRoot()?.nativeElement;

    if (footer && sidebar) {
      const footerOffset = footer.offsetTop;
      const sidebarHeight = sidebar.clientHeight;
      const maxOffset = footerOffset - sidebarHeight;
      const currentScrollPosition = scrollTop + Application.TWENTY * 10;

      // Use hysteresis to prevent flickering near the threshold
      if (this.isSticky) {
        // When already sticky, only switch off when scrolled significantly above threshold
        if (currentScrollPosition < maxOffset - this.STICKY_HYSTERESIS) {
          this.isSticky = false;
        }
      } else {
        // When not sticky, only switch on when scrolled significantly past threshold
        if (currentScrollPosition > maxOffset + this.STICKY_HYSTERESIS) {
          this.isSticky = true;
        }
      }
    } else {
      this.isSticky = false;
    }

    if (!this.isSticky) {
      this.renderer?.removeClass(
        this.document.querySelector('.main-content'),
        'remove-left-margin'
      );
    } else {
      this.renderer?.addClass(
        this.document.querySelector('.main-content'),
        'remove-left-margin'
      );
    }
  }
  getWalletRoute() {
    const permissionRoutes: { [key: string]: string } = {
      VIEW_MY_WALLET_BALANCE: '/wallet',
      ADD_FUND_TO_THE_WALLET: '/wallet',
      WITHDRAW_MONEY_TO_MY_BANK_ACCOUNT: '/wallet',
      ADD_REMOVE_BANK_ACCOUNT_AND_CREDIT_CARD: '/wallet',
      VIEW_TRANSACTION_HISTORY: '/wallet/transaction-history'
    };

    const permissionChecks = Object.entries(permissionRoutes).map(
      ([key, route]) => {
        return this.ps
          .hasPermission(this.permissionKeys[key], PERMISSION_TYPE.READ, true)
          .pipe(map((hasPermission) => (hasPermission ? route : null)));
      }
    );

    combineLatest(permissionChecks)
      .pipe(
        map(
          (routes) =>
            routes.find((route) => route !== null) || '/wallet/my-purchases'
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((finalRoute) => {
        this.walletRoute = finalRoute;
      });
  }
  userProfileSubscription(): void {
    this.store
      .select(fromUserProfile.getUserProfileItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (!this.authService.isLogin) {
          this.resetGuestSidebarState();
          return;
        }
        const profile = res as any;
        const isProfileReady = !!(
          profile?.gsi4pk ||
          profile?.user_name ||
          profile?.id
        );
        if (isProfileReady) {
          this.isSidebarLoading = false;
        }
        this.userProfile = profile
          ? {
              ...profile,
              isAbove18: this.cs.isUser18Above(profile.birth_date)
            }
          : profile;
        this.isCreatorRequestResubmitted =
          this.userProfile?.id_verification_status ===
          this.applicationStatusEnums.RESUBMIT;
        this.setApplicationRejectModal();
      });
  }

  get isEditPreferencePage(): boolean {
    return this.router.url.startsWith('/edit-preference');
  }

  private subscribeToForceLogout(): void {
    this.cs.logout.pipe(takeUntil(this.destroy$)).subscribe((shouldLogout) => {
      if (shouldLogout) {
        this.resetGuestSidebarState();
      }
    });
  }

  private resetGuestSidebarState(): void {
    this.userProfile = null;
    this.isSidebarLoading = false;
    this.totalResourcesCount = Application.ZERO;
    this.userBadges = {
      messageCount: '',
      unreadActivityCount: '',
      my_fulfillment_requests_count: '',
      refund_requests_count: '',
      merchandise_awaiting_fan_confirmation_count: '',
      experience_awaiting_fan_confirmation_count: ''
    };
  }

  getBadges() {
    this.store
      .select(fromUserProfile.getRefreshStateItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (!this.authService.isLogin) {
          return;
        }
        if (Object.keys(res).length) {
          this.userBadges = {
            messageCount: res?.unreadMessage
              ? this.cs.kFormatter(res?.unreadMessage)
              : '',
            unreadActivityCount: res?.unread_activity_count,
            my_fulfillment_requests_count: res?.my_fulfillment_requests_count,
            refund_requests_count: res?.refund_requests_count,
            merchandise_awaiting_fan_confirmation_count:
              res?.merchandise_awaiting_fan_confirmation_count,
            experience_awaiting_fan_confirmation_count:
              res?.experience_awaiting_fan_confirmation_count
          };
          this.coreService.messageCount.next(
            res?.unreadMessage ? res?.unreadMessage : ''
          );
        }
      });
  }

  get getRouteStatusForMonetize() {
    return this.router.url.includes(SIDE_BAR_MODULE.MONETIZE);
  }
  get getRouteStatusForResources() {
    return this.router.url.includes(SIDE_BAR_MODULE.RESOURCES);
  }

  getRouteStatusForWallet() {
    return this.router.url.includes('/wallet');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMessageCount() {
    this.coreService.messageCount
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.userBadges.messageCount = res ? this.cs.kFormatter(res) : '';
      });
  }

  resetCreatorSubTabs() {
    this.cs.activeCreatorTab.next('Creators');
    this.cs.creatorSubTab.next('following');
  }

  onRestrictedNavClick(event: Event): void {
    if (!this.showGuestMobileNav) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.openLoginDialog();
  }

  openLoginDialog(): void {
    this.modalService.show(LoginDialogComponent, {});
  }

  get isCreatorRequestPending() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.PENDING
    );
  }

  get isCreatorRequestFailed() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.FAILED
    );
  }

  get isCreatorRequestApproved() {
    return (
      this.userProfile?.admin_approval_status ===
      this.applicationStatusEnums.APPROVED
    );
  }

  navigateToBecomeACreatorPage() {
    if (this.isCreatorRequestResubmitted) {
      this.router.navigate([appRoutingUrl.BECOME_A_CREATOR]);
      return;
    }
    if (this.isCreatorRequestPending) {
      this.openVeriffStatusModals();
    }
    if (this.isCreatorRequestFailed) {
      this.openModal(this.applicationRejectModalField);
    } else if (!this.userProfile?.admin_approval_status) {
      this.creatorRequirementsModal()?.openModal();
    }
  }

  private initVerificationModals(): void {
    this.failureModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      heading: 'Identity Verification Failed',
      description: `Unfortunately, your identity was not able to be verified. <br>For this reason we can\'t evaluate your application to Become a Creator. <br> If you think this is a mistake, please contact <a href="${environment.websiteURL}/help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.`,
      iconStyleClass: 'red-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };

    this.successModalField = {
      icon: './assets/images/svg/checkmark-outline.svg',
      heading: 'Your Identity Has Been Verified',
      description:
        'The TopFan team will now evaluate your application to Become a Creator and you will typically receive a status update by email within one business day.',
      iconStyleClass: 'confirm-creator-icon-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };

    this.pendingModalField = {
      icon: './assets/images/svg/pending.svg',
      heading: 'Identity Verification Pending',
      description:
        'Your identity verification is currently in progress. We are evaluating your application to Become a Creator. Please be patient as this process may take some time. If you have any questions, please contact <a href="' +
        environment.websiteURL +
        'help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.',
      iconStyleClass: 'yellow-bg',
      buttonField: {
        type: 'button',
        class: 'btn btn-primary',
        label: 'Done'
      }
    };
    this.setApplicationRejectModal();
  }

  private setApplicationRejectModal(): void {
    this.applicationRejectModalField = {
      icon: './assets/images/svg/red-info-blank.svg',
      iconStyleClass: 'red-bg',
      heading: 'Your application has been declined by our team. ',
      description: `Reason: ${this.userProfile?.admin_action_reason}<br><br> If you think this is a mistake, please contact <a href="${environment.websiteURL}help-faq" target="_blank" class="highlight">Customer&nbsp;Support</a>.`
    };
  }

  openVeriffStatusModals() {
    const verification_status = this.userProfile?.id_verification_status;
    switch (verification_status) {
      case VERIFICATION_STATUS.APPROVED:
        this.openModal(this.successModalField);
        break;
      case VERIFICATION_STATUS.PENDING:
        this.openModal(this.pendingModalField);
        break;
      default:
        this.openModal(this.failureModalField);
        break;
    }
  }

  openModal(modalField: any) {
    this.modalService.show(InfoModalComponent, {
      initialState: modalField,
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  navigateToLiveStream() {
    if (
      this.userProfile?.is_live &&
      this.userProfile?.livestream_id &&
      this.userProfile?.user_name
    ) {
      this.router.navigate([
        `dashboard/live-stream/${this.userProfile?.user_name}/${this.userProfile?.livestream_id}`
      ]);
    } else {
      this.router.navigate(['/dashboard/live-stream']);
    }
  }

  setInsightsState(): void {
    forkJoin({
      earning: this.ps
        .hasPermission(
          this.permissionKeys.VIEW_EARNING_METRICS,
          PERMISSION_TYPE.READ,
          true
        )
        .pipe(take(1)),
      engagement: this.ps
        .hasPermission(
          this.permissionKeys.VIEW_ENGAGEMENT_METRICS,
          PERMISSION_TYPE.READ,
          true
        )
        .pipe(take(1)),
      segmentation: this.ps
        .hasPermission(
          this.permissionKeys.ACCESS_USER_SEGMENTATION,
          PERMISSION_TYPE.READ,
          true
        )
        .pipe(take(1)),
      fanMgmt: this.ps
        .hasPermission(
          this.permissionKeys.FAN_MANAGEMENT,
          PERMISSION_TYPE.READ,
          true
        )
        .pipe(take(1))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((perms) => {
        let targetTab = INSIGHT_TAB.EARNING;

        if (perms.earning) {
          targetTab = INSIGHT_TAB.EARNING;
        } else if (perms.engagement) {
          targetTab = INSIGHT_TAB.ENGAGEMENT;
        } else if (perms.segmentation || perms.fanMgmt) {
          targetTab = INSIGHT_TAB.FAN_RELATIONSHIP_MANAGEMENT;
        }

        this.insightsStateService.tabType = targetTab;
        this.insightsStateService.frmTabType = INSIGHT_TAB.FAN_MANAGEMENT;
        this.insightsStateService.fanTabKey = INSIGHT_TAB.ALL;
        this.insightsStateService.fanManagementDateRange = null;
        this.insightsStateService.selectedFanFilter = USER_ACTIONS.ALL;
        this.router.navigate([ROUTE_PATHS.INSIGHTS_PATH], {
          queryParams: { tab: targetTab }
        });
      });
  }

  isActive(url: string): boolean {
    return this.router.url?.includes(url);
  }
}
